<template>
  <!-- Busy time in Google Calendar taking times off a booking page. Top: a team member's Google
       Calendar day (sample events). Bottom: the time step of their public booking page
       (workspace-public-website locales/booking/en.json: "Select a time", "{service} · {minutes}
       min with {member}"). Beats: the Google events land, the booking times appear, the times the
       busy event covers fade out while the event marked Free leaves its time open, then the
       app's own rule settles (app_en.arb settingsIntegrationsCalendarConflictsHelper, first
       sentence). The calendar name follows settingsIntegrationsCalendarPrimary. Server-rendered; resting styles are the final frame. Sample data only. -->
  <div class="gcb">
    <div class="gcb-pane fm-rise" style="--at: 0.04">
      <p class="gcb-label"><span class="gcb-g" translate="no">Work (Primary)</span><span translate="no">Tue, Nov 3</span></p>
      <div class="gcb-event gcb-event--free fm-rise" style="--at: 0.1">
        <b translate="no">Focus time</b><em translate="no">Free</em><span translate="no">10:00 – 10:30 AM</span>
      </div>
      <div class="gcb-event gcb-event--busy fm-rise fm-focus" style="--at: 0.16; --grow: 2px; --radius: 8px">
        <b translate="no">Board meeting</b><em translate="no">Busy</em><span translate="no">1:00 – 2:30 PM</span>
      </div>
    </div>

    <div class="gcb-link fm-in" style="--at: 0.24" aria-hidden="true"><i /></div>

    <div class="gcb-pane gcb-pane--page fm-rise" style="--at: 0.26">
      <p class="gcb-title">Select a time</p>
      <p class="gcb-sub" translate="no">Strategy session · 60 min with Priya Raman</p>
      <div class="gcb-slots" translate="no">
        <span v-for="slot in slots" :key="slot.t" class="gcb-slot" :class="{ 'is-busy': slot.busy, 'is-free': slot.free }">
          <span v-if="slot.busy" class="gcb-slot-t fm-out" :style="{ '--at': 0.4 + slot.i * 0.04 }">{{ slot.t }}<small>{{ slot.m }}</small></span>
          <span v-else class="gcb-slot-t">{{ slot.t }}<small>{{ slot.m }}</small></span>
          <span v-if="slot.busy" class="gcb-gap fm-in" :style="{ '--at': 0.44 + slot.i * 0.04 }" />
        </span>
      </div>
    </div>

    <p class="gcb-rule fm-rise" style="--at: 0.6">Busy events on these calendars block matching times on your public booking pages.</p>
  </div>
</template>

<script setup>
const slots = [
  { t: "9:00", m: "AM" },
  { t: "9:30", m: "AM" },
  { t: "10:00", m: "AM", free: true },
  { t: "11:00", m: "AM" },
  { t: "1:00", m: "PM", busy: true, i: 0 },
  { t: "1:30", m: "PM", busy: true, i: 1 },
  { t: "2:00", m: "PM", busy: true, i: 2 },
  { t: "3:00", m: "PM" },
];
</script>

<style>
.gcb { display: grid; gap: 8px; }
.gcb-pane { border: 1px solid var(--fm-line); border-radius: 8px; background: var(--fm-sunken); padding: 9px 10px; }
.gcb-pane--page { background: var(--fm-surface); }
.gcb-label { display: flex; justify-content: space-between; margin: 0 0 6px; color: var(--fm-muted); font-size: 11px; font-weight: 600; }
.gcb-g { color: var(--fm-text); }
.gcb-event { display: grid; grid-template-columns: 1fr auto; align-items: center; column-gap: 8px; margin-top: 5px; border-left: 3px solid #1a73e8; border-radius: 6px; background: var(--fm-surface); padding: 6px 8px; font-size: 11.5px; }
.gcb-event b { font-weight: 600; }
.gcb-event span { grid-column: 1 / -1; color: var(--fm-muted); font-size: 11px; font-variant-numeric: tabular-nums; }
.gcb-event em { border-radius: 4px; padding: 1px 6px; font-size: 10px; font-style: normal; font-weight: 700; }
.gcb-event--free { border-left-color: var(--fm-faint); }
.gcb-event--free em { background: var(--fm-gray-fill); color: var(--fm-gray); }
.gcb-event--busy em { background: var(--fm-blue-fill); color: var(--fm-blue); }
.gcb-link { display: grid; place-items: center; height: 6px; }
.gcb-link i { width: 2px; height: 14px; margin-top: -4px; border-radius: 1px; background: var(--fm-line); }
.gcb-title { margin: 0; color: var(--fm-text); font-size: 13px; font-weight: 700; }
.gcb-sub { margin: 1px 0 0; color: var(--fm-muted); font-size: 11px; }
.gcb-slots { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 5px; margin-top: 8px; }
.gcb-slot { position: relative; display: grid; border: 1px solid var(--fm-line); border-radius: 6px; padding: 6px 0; text-align: center; font-size: 11.5px; font-weight: 600; font-variant-numeric: tabular-nums; }
.gcb-slot > * { grid-area: 1 / 1; }
.gcb-slot small { margin-left: 2px; color: var(--fm-muted); font-size: 8.5px; font-weight: 500; }
.gcb-slot.is-busy { border-color: transparent; }
/* A busy time leaves an empty, dashed cell so the grid stays still. */
.gcb-gap { position: absolute; inset: -1px; border: 1px dashed var(--fm-line); border-radius: 6px; }
.gcb-slot.is-free { border-color: var(--fm-green); }
.gcb-rule { margin: 2px 0 0; border-radius: 8px; background: var(--fm-blue-fill); padding: 8px 10px; color: var(--fm-blue); font-size: 11.5px; font-weight: 600; line-height: 1.4; }
</style>
